import type { CsrfToken, Problem, ProblemCode } from '@/types/api'
import { AbortError, ApiError, NetworkError } from './problem'

/**
 * 唯一的 HTTP 出口。AGENTS.md 2 明确禁止在组件里裸写 fetch/axios，
 * 因此本文件之外不应出现第二处 `fetch(`（`src/api/**` 内的模块都经由这里）。
 *
 * 这个封装解决四件后端契约强制要求、又不该散落到各处的事：
 *
 * 1. **基址与 Cookie。** 两者都由 `VITE_API_BASE_URL` 一个变量决定：留空是相对
 *    路径 `/api/v1`（同站部署，开发期由 vite 代理保持同源），填绝对地址就是
 *    前后端分离。`credentials` 从基址**推导**而不是另设开关，见 `CREDENTIALS`
 *    上的注释 —— 跨源部署对后端有三条硬性要求也写在那里。
 * 2. **CSRF。** 所有不安全方法都必须带 `X-CSRF-Token` 与精确 `Origin` ——
 *    **包括注册、登录、阅读事件、下载会话这些游客也能发起的 POST**。Token 与
 *    当前 Cookie 绑定，这里做缓存 + 过期前刷新 + 403 时重取一次。
 * 3. **problem+json。** 所有错误都是 RFC 9457，统一转成 ApiError，让上层按
 *    `code` 分支。
 * 4. **未知参数一律 422。** 后端不静默忽略未知查询参数，因此序列化时必须
 *    丢弃 `undefined`／空数组，绝不能把 `?cursor=undefined` 发出去。
 */

/**
 * 请求前缀。全局唯一的一处，由 `VITE_API_BASE_URL` 控制（见 .env）。
 *
 * 默认是**相对路径** `/api/v1`：同站部署（反向代理把 /api 转给后端）时这是对的，
 * 开发期则由 vite 代理保持同源。
 *
 * 填成跨源的绝对地址（`https://api.example.com/api/v1`）即前后端分离部署。
 * 下面的 `CREDENTIALS` 会自动切到 `include`，但**后端必须同时改三处**才能建立
 * 登录态（CORS 允许凭据、回显确切 Origin、会话 Cookie 改 `SameSite=None; Secure`），
 * 否则表现是「接口全通但一直 401」。三条的完整说明见 `CREDENTIALS`。
 *
 * 末尾的 `/` 统一去掉：拼接时路径自带前导 `/`，否则会出现 `//posts`。
 */
const API_BASE = (import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/+$/, '')

/**
 * 请求是否跨源。由 `API_BASE` **推导**，不是第二个开关。
 *
 * 两种部署形态都要支持：同站反向代理（`API_BASE` 是相对路径）与前后端分离
 * （`API_BASE` 是另一个源的绝对地址）。它们唯一的区别就是 `credentials` 该取
 * 哪个值，而这件事从 `API_BASE` 就能唯一确定 —— 再加一个 `VITE_API_CROSS_ORIGIN`
 * 之类的变量只会制造「两个变量互相矛盾」这一类新的错误配置。
 *
 * `new URL(base, location.href)` 对相对路径与绝对地址一视同仁：前者解析到当前
 * 源（于是 origin 相等），后者解析到它自己的源。
 */
const IS_CROSS_ORIGIN = ((): boolean => {
  try {
    return new URL(API_BASE, location.href).origin !== location.origin
  } catch {
    // 解析不了就按同源处理：这是更保守的一侧（不会把 Cookie 发给意料之外的源）。
    return false
  }
})()

/**
 * 携带凭据的方式。
 *
 * `same-origin` 在跨源时**不发送 Cookie**，而失败方式极具迷惑性：请求通、
 * 没有 CORS 报错、接口一律 401。所以跨源时必须是 `include`。
 *
 * 反过来，同源时不用 `include`，因为 `include` 会连带要求后端对预检与响应都
 * 给出 `Access-Control-Allow-Credentials`，同源部署下白白多一层约束。
 *
 * ⚠️ 跨源（跨站）时**后端必须同时满足三件事**，缺一件登录态就建立不起来：
 *
 *   1. 会话 Cookie 去掉 `__Host-` 前缀要求的同站语义，改成
 *      `SameSite=None; Secure` —— `SameSite=Lax` 的 Cookie 在跨站 fetch 里
 *      浏览器根本不会发出，与这里配什么无关；
 *   2. `Access-Control-Allow-Origin` 回显前端确切的源（不能是 `*`，带凭据时
 *      通配符无效），并且 `Access-Control-Allow-Credentials: true`；
 *   3. `Access-Control-Expose-Headers` 至少包含 `ETag` 与 `Location` ——
 *      跨源响应默认只暴露七个安全头，读不到 `ETag` 会让所有版本化写操作
 *      （If-Match）在拿不到 ETag 这一步就断掉，且浏览器不给任何提示。
 *
 * 另外后端的 CSRF Origin 白名单（`MODSITE_ALLOWED_ORIGINS`）要包含前端域名。
 */
const CREDENTIALS: RequestCredentials = IS_CROSS_ORIGIN ? 'include' : 'same-origin'

/** 安全方法不需要 CSRF Token（后端只对不安全方法校验）。 */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** 查询参数：数组按 `explode: true` 展开成重复键，`undefined` 与空数组丢弃。 */
export type QueryValue = string | number | boolean | undefined | null | string[]
export type QueryParams = Record<string, QueryValue>

export interface RequestOptions {
  method?: HttpMethod
  query?: QueryParams
  /** JSON 请求体。与 `formData` 互斥。 */
  body?: unknown
  /** multipart/form-data 请求体（媒体上传）。浏览器自动带 boundary，不要手写 Content-Type。 */
  formData?: FormData
  signal?: AbortSignal
  /** 版本化实体的写操作必须携带：缺失 428，过期 412。 */
  ifMatch?: string
  /** 下载会话与上传会话创建需要。 */
  idempotencyKey?: string
  /** `Authorization: DownloadSession <token>`，只用于刷新下载链接。 */
  downloadSessionToken?: string
  /**
   * `X-Turnstile-Token`，只用于注册与创建下载会话。
   *
   * token 一次性且约 5 分钟过期，所以**每次尝试都要传一个新的**，
   * 不能把上一次的存下来重发。见 src/api/turnstile.ts。
   */
  turnstileToken?: string
  /** 额外响应头透出（例如读取 ETag 供后续 If-Match 使用）。 */
  wantHeaders?: boolean
}

export interface ResponseEnvelope<T> {
  data: T
  status: number
  etag: string | undefined
  location: string | undefined
}

/* --------------------------------------------------------------- 查询序列化 */

export function buildQuery(params: QueryParams | undefined): string {
  if (!params) return ''
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      // explode: true —— 每个值一个同名键；空数组不产生任何键。
      for (const item of value) {
        if (item !== '') search.append(key, item)
      }
      continue
    }
    search.append(key, String(value))
  }
  const serialized = search.toString()
  return serialized ? `?${serialized}` : ''
}

/* ------------------------------------------------------------------- CSRF */

interface CachedCsrf {
  token: string
  headerName: string
  expiresAt: number
}

/** 提前 30 秒判定过期，避免请求正好卡在边界上。 */
const CSRF_SKEW_MS = 30_000

let csrfCache: CachedCsrf | null = null
let csrfInflight: Promise<CachedCsrf> | null = null

async function fetchCsrf(signal?: AbortSignal): Promise<CachedCsrf> {
  const response = await rawFetch(`${API_BASE}/auth/csrf-token`, {
    method: 'GET',
    credentials: CREDENTIALS,
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  })
  if (!response.ok) throw await toApiError(response)
  const payload = (await response.json()) as CsrfToken
  return {
    token: payload.csrf_token,
    headerName: payload.header_name || 'X-CSRF-Token',
    expiresAt: new Date(payload.expires_at).getTime(),
  }
}

async function getCsrf(signal?: AbortSignal): Promise<CachedCsrf> {
  const cached = csrfCache
  if (cached && cached.expiresAt - CSRF_SKEW_MS > Date.now()) return cached
  // 并发的不安全请求共享同一次取 Token，避免打出一串 /auth/csrf-token。
  csrfInflight ??= fetchCsrf(signal)
    .then((fresh) => {
      csrfCache = fresh
      return fresh
    })
    .finally(() => {
      csrfInflight = null
    })
  return csrfInflight
}

/**
 * 主动作废 CSRF 缓存。
 * 登录、登出、改密码都会换掉会话 Cookie，而 Token 与 Cookie 绑定，
 * 旧 Token 对新会话无效，必须丢弃。
 */
export function invalidateCsrfToken(): void {
  csrfCache = null
}

/**
 * 用响应里交还的 Token 直接替换缓存。
 *
 * 登录 / 登出 / 改密码这三个响应各自带一份新 Token（`LoginResponse.csrf`、
 * `CsrfTokenEnvelope.csrf`）—— 后端为此刻意让它们返回 200 而不是 204。
 * 采纳它比 `invalidateCsrfToken()` 更好：后者会让下一个写操作先多打一次
 * `GET /auth/csrf-token`，而那一次往返在响应体里已经付过了。
 */
export function adoptCsrfToken(payload: CsrfToken): void {
  csrfCache = {
    token: payload.csrf_token,
    headerName: payload.header_name || 'X-CSRF-Token',
    expiresAt: new Date(payload.expires_at).getTime(),
  }
}

/* --------------------------------------------------------------- 错误归一化 */

function synthesizeProblem(status: number, code: ProblemCode, title: string): Problem {
  return { type: `urn:modsite:problem:${code}`, title, status, code }
}

async function toApiError(response: Response): Promise<ApiError> {
  const contentType = response.headers.get('Content-Type') ?? ''
  if (contentType.includes('application/problem+json') || contentType.includes('application/json')) {
    try {
      const problem = (await response.json()) as Partial<Problem>
      if (problem && typeof problem.code === 'string') {
        return new ApiError({
          type: problem.type ?? `urn:modsite:problem:${problem.code}`,
          title: problem.title ?? response.statusText,
          status: problem.status ?? response.status,
          code: problem.code as ProblemCode,
          ...(problem.detail !== undefined ? { detail: problem.detail } : {}),
          ...(problem.instance !== undefined ? { instance: problem.instance } : {}),
          ...(problem.errors !== undefined ? { errors: problem.errors } : {}),
          ...(problem.retry_after_seconds !== undefined
            ? { retry_after_seconds: problem.retry_after_seconds }
            : {}),
        })
      }
    } catch {
      // 响应体不是合法 JSON，落到下面按状态码合成。
    }
  }
  // 后端承诺所有错误都是 problem+json；走到这里说明是代理/网关返回的错误页。
  const byStatus: Record<number, ProblemCode> = {
    400: 'invalid_request',
    401: 'authentication_required',
    403: 'forbidden',
    404: 'not_found',
    409: 'state_conflict',
    410: 'gone',
    412: 'precondition_failed',
    422: 'validation_failed',
    428: 'precondition_required',
    429: 'rate_limited',
  }
  const code = byStatus[response.status] ?? 'internal_error'
  return new ApiError(synthesizeProblem(response.status, code, response.statusText || '请求失败'))
}

/* -------------------------------------------------------------------- 核心 */

/** 抽出来只为让 fetchCsrf 与 request 共用同一处网络错误归一化。 */
async function rawFetch(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw new AbortError()
    throw new NetworkError()
  }
}

async function send(path: string, options: RequestOptions, csrfRetried: boolean): Promise<Response> {
  const method = options.method ?? 'GET'
  const headers = new Headers({ Accept: 'application/json' })

  let body: BodyInit | undefined
  if (options.formData) {
    body = options.formData // Content-Type 由浏览器带 boundary 生成
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json')
    body = JSON.stringify(options.body)
  }

  if (!SAFE_METHODS.has(method)) {
    const csrf = await getCsrf(options.signal)
    headers.set(csrf.headerName, csrf.token)
  }
  if (options.ifMatch) headers.set('If-Match', options.ifMatch)
  if (options.idempotencyKey) headers.set('Idempotency-Key', options.idempotencyKey)
  if (options.downloadSessionToken) {
    headers.set('Authorization', `DownloadSession ${options.downloadSessionToken}`)
  }
  if (options.turnstileToken) headers.set('X-Turnstile-Token', options.turnstileToken)

  const response = await rawFetch(`${API_BASE}${path}${buildQuery(options.query)}`, {
    method,
    headers,
    credentials: CREDENTIALS, // 会话 Cookie 必须随请求发出；跨源时是 'include'
    redirect: 'follow',
    ...(body !== undefined ? { body } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  })

  // CSRF Token 与 Cookie 绑定：会话被顶替后旧 Token 会 403。重取一次再试，
  // 只重试一次，避免在真正的权限不足上打循环。
  //
  // 但 403 不只有 CSRF 一种：人机验证失败也是 403。那种情况**绝不能重放** ——
  // Turnstile token 一次性，重发同一个只会白白烧掉它，然后以一个更难懂的
  // `rejected` 收场。所以先看一眼 `code` 再决定。
  if (response.status === 403 && !SAFE_METHODS.has(method) && !csrfRetried) {
    if ((await peekProblemCode(response)) !== 'challenge_failed') {
      invalidateCsrfToken()
      return send(path, options, true)
    }
  }
  return response
}

/**
 * 不消费响应体地看一眼 `Problem.code`。
 *
 * 用 `clone()`：响应体是一次性流，直接读掉的话后面的 `toApiError` 就拿不到
 * `detail` 与字段错误了。
 */
async function peekProblemCode(response: Response): Promise<string | undefined> {
  if (!(response.headers.get('Content-Type') ?? '').includes('json')) return undefined
  try {
    const problem = (await response.clone().json()) as Partial<Problem>
    return typeof problem.code === 'string' ? problem.code : undefined
  } catch {
    return undefined
  }
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ResponseEnvelope<T>> {
  const response = await send(path, options, false)

  if (!response.ok) throw await toApiError(response)

  const envelope = {
    status: response.status,
    etag: response.headers.get('ETag') ?? undefined,
    location: response.headers.get('Location') ?? undefined,
  }

  // 204 与 304 没有响应体。
  if (response.status === 204 || response.status === 304) {
    return { data: undefined as T, ...envelope }
  }
  const contentType = response.headers.get('Content-Type') ?? ''
  if (!contentType.includes('json')) {
    return { data: undefined as T, ...envelope }
  }
  return { data: (await response.json()) as T, ...envelope }
}

/**
 * 取一段二进制响应体（管理端图片预览）。
 *
 * 画布要用的图片必须走这里拿成 `Blob` 再 `createObjectURL`，不能直接把
 * `apiPath(...)` 塞给 `new Image()`：API 可能与前端跨源，一张没带 CORS 的跨源图
 * 画进画布会把画布「污染」，之后 `toBlob` 直接抛 SecurityError —— 用户点完
 * 「完成」什么也得不到。`fetch` 走 CORS 并带 Cookie，拿到的 Blob URL 是同源的。
 */
export async function requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const response = await send(path, options, false)
  if (!response.ok) throw await toApiError(response)
  return response.blob()
}

/** 只关心响应体时的快捷方式。 */
export async function requestData<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { data } = await request<T>(path, options)
  return data
}

/**
 * 生成 `Idempotency-Key`。
 *
 * 后端把键绑定到 subject + 路由模板 + 规范化请求体哈希，重放会返回同一个业务
 * 资源。因此**同一个用户意图必须复用同一个键**，不能每次点击都换新的 —— 调用方
 * 负责把键存起来（见 src/features/download/useDownloadSession.ts）。
 */
export function newIdempotencyKey(): string {
  // `crypto.randomUUID` 只在安全上下文可用（https 与 localhost 都算），
  // 正是本站的部署形态 —— 后端的 `__Host-` Cookie 本身就要求 Secure。
  // 非安全上下文下退回 getRandomValues，仍然是密码学随机。
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * 把一个契约路径拼成浏览器可直接引用的地址。
 *
 * 用于 `<img src>` 这类**不经过 `request()`** 的场景。存在的理由是基址只有
 * `API_BASE` 一处：任何地方写死 `/api/v1`，都会在改过 `VITE_API_BASE_URL`
 * 的部署里变成一个 404，而且通常只表现为「图片坏了、接口都正常」。
 */
export function apiPath(path: string): string {
  return `${API_BASE}${path}`
}

/** 公开图片路径。出图路由固定 `no-cache` + 强 ETag，浏览器可缓存但每次重新验证。 */
export function mediaUrl(publicKey: string): string {
  return apiPath(`/media/${encodeURIComponent(publicKey)}`)
}
